import {
  ANALYTICS_CLUSTER_MIN_FAILURES,
  ANALYTICS_SLOW_ROUTE_MIN_REQUESTS,
  OFFICER_LOCATION_MAX_ENTRIES,
  OFFICER_TRAIL_MAX_ENTRIES,
  type AnalyticsActorRole,
  type AnalyticsAuthFailureKind,
  type AnalyticsGrain,
  type AnalyticsWindow,
} from '@pams/shared';
import { ACCESS_ROLE_LABEL } from '@/lib/labels';
import { formatCount, type LatencyText } from '@/lib/analyticsReport';
import type { UsageHoursBasis } from '@/lib/usageHours';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  EVERY WORD THE USAGE ANALYTICS PAGE SAYS. [ANALYTICS §5]
 *
 *  One file, so prose carries a test and cannot be deleted in a tidy-up without something going
 *  red — `DASHBOARD_COPY`'s stated reason.
 *
 *  ⚠️ EVERY SIGNAL SAYS WHETHER IT IS NEW. Four of the numbers on this page were never counted
 *  before this release — refresh-token reuse, OTP bans set, sign-ins and refusals by kind per
 *  address — so a window reaching back before the release shows zeros that are not "nothing
 *  happened". The one read of an EXISTING record (the lockouts active now) says so too, so the
 *  two kinds cannot be confused.
 *
 *  ⚠️ AND THE LIMITS ARE PLAIN SENTENCES ON THE PAGE, NEVER A TOOLTIP. A caveat an operator has
 *  to hover to find is one they will not find — and hover is unavailable on touch and
 *  unreachable from a keyboard.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const plural = (count: number, one: string, many: string): string =>
  `${formatCount(count)} ${count === 1 ? one : many}`;

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

/** A series point's grain as a word — "the busiest hour", never "the busiest point". */
const GRAIN_WORD: Record<AnalyticsGrain, string> = { minute: 'minute', hour: 'hour', day: 'day' };

/** The peak a series summary names: its IST label and everything in it. */
type Peak = { label: string; total: number } | null;

const twoDigits = (hour: number): string => String(hour).padStart(2, '0');

/** `10:00–11:00`; the last hour of the day ends at `00:00`. */
const hourSpan = (hour: number): string => `${twoDigits(hour)}:00–${twoDigits((hour + 1) % 24)}:00`;

/** An average is a fraction; a whole number above ten reads better without its tenth. */
const averageFigure = (value: number): string =>
  value >= 10 ? formatCount(Math.round(value)) : value.toLocaleString('en-IN', { maximumFractionDigits: 1 });

export const USAGE_ANALYTICS_COPY = {
  title: 'Usage analytics',
  updated: (time: string) => `Updated ${time} IST`,
  updating: 'Loading this window…',
  refreshFailed: (time: string) => `Could not refresh — showing ${time} IST`,
  loading: 'Loading usage analytics',
  loadingSection: (title: string) => `Loading ${title.toLowerCase()}`,
  errorTitle: 'Usage analytics could not be loaded',
  errorDescription: 'Nothing on this page was read. Try again in a moment.',
  retry: 'Try again',
  windowLabel: 'Window',
  windows: { '30m': '30 min', '1d': '1 day', '7d': '7 days', '30d': '30 days', '90d': '90 days' } satisfies Record<
    AnalyticsWindow,
    string
  >,

  sectionFailure: 'This section could not be loaded.',

  overview: {
    title: 'Overview',
    requests: 'Requests',
    uniqueOfficers: 'Unique officers',
    sessions: 'Sessions',
    signIns: 'Sign-ins',
    estimate: (value: number | null) => (value === null ? '—' : `≈ ${formatCount(value)}`),
    estimateCaption: 'Estimated',
    countedSinceRelease: 'Counted since this release',
    uniquesUnavailable: 'Unique officers and sessions could not be counted: the counter store did not answer.',
    surfaceHeading: 'By app',
    surfaceColumn: 'Measure',
    dashboard: 'Dashboard',
    officerApp: 'Officer app',
    surfaceCaption: 'Duty Mitra and the native app are counted together as "Officer app".',
    /*
     * ⚠️ "NO VALID SIGN-IN", NEVER "BEFORE SIGNING IN". The capture records surface and role `none`
     * for every request without a verified token — the sign-in itself, but also every session
     * refresh (`POST /auth/refresh` carries no access token) and every request an expired or forged
     * token was refused on, which a signed-in officer produces every half hour. Called "before
     * signing in", the routine half of that reads as a crowd at the door.
     */
    anonymous: (count: number) =>
      count === 0
        ? null
        : `${plural(count, 'request', 'requests')} carried no valid sign-in. That includes signing in, refreshing a session and every request made with an expired token.`,
    roleHeading: 'Requests by role',
    role: (role: AnalyticsActorRole) => (role === 'none' ? 'No valid sign-in' : ACCESS_ROLE_LABEL[role]),
    noRoles: 'No requests in this window.',
    seriesTitle: 'Requests over time',
    seriesName: 'Requests',
    seriesSummary: (total: number, peak: Peak, grain: AnalyticsGrain) =>
      peak === null
        ? 'No requests in this window.'
        : `${plural(total, 'request', 'requests')} in this window. The busiest ${GRAIN_WORD[grain]} was ${peak.label}, with ${plural(peak.total, 'request', 'requests')}.`,
    coverageGap: (percent: number) =>
      `${String(percent)}% of minutes in this window were captured. The rest are a gap in the record, not quiet minutes.`,
    coverageUnread: 'This window could not be read, so how much of it the record holds is not known.',
    dataThrough: (time: string) => `Includes data through ${time} IST.`,
    dropped: (count: number) =>
      `${plural(count, 'request', 'requests')} could not be recorded while the counter store was unreachable, so the figures are short by at least that many.`,
  },

  usageHours: {
    title: 'Usage hours',
    weekdaysShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const,
    basis: {
      average:
        'Each cell is the average number of requests on one date of that weekday, in IST. Today counts only towards the hours that have begun.',
      total: 'Each cell is the total number of requests in this window, in IST.',
    } satisfies Record<UsageHoursBasis, string>,
    busiest: (basis: UsageHoursBasis, weekday: number, hour: number, value: number) =>
      basis === 'average'
        ? `Busiest: ${WEEKDAYS[weekday] ?? ''} ${hourSpan(hour)} IST (about ${averageFigure(value)} requests on an average ${WEEKDAYS[weekday] ?? ''}).`
        : `Busiest: ${WEEKDAYS[weekday] ?? ''} ${hourSpan(hour)} IST (${plural(value, 'request', 'requests')}).`,
    daytime: (percent: number, from: number, to: number) =>
      `${String(percent)}% of requests fall between ${twoDigits(from)}:00 and ${twoDigits(to)}:00.`,
    none: 'No requests in this window.',
    tableCaption: (basis: UsageHoursBasis) =>
      basis === 'average'
        ? 'Average requests per date, by weekday and IST hour'
        : 'Requests in this window, by weekday and IST hour',
    dayHeader: 'Day',
    hourHeader: (hour: number) => `${twoDigits(hour)}:00`,
    hourTick: (hour: number) => twoDigits(hour),
    weekday: (index: number) => WEEKDAYS[index] ?? '',
    cellFigure: (basis: UsageHoursBasis, value: number) =>
      basis === 'average' ? averageFigure(value) : formatCount(value),
  },

  geography: {
    title: 'Geography',
    /*
     * ⚠️ [amended C5b §4] "OPEN", NOT "CONFIGURED". `lookupEnabled` is whether the database OPENED in
     * this process, so the sentence says that — a server configured with a missing file reads it too,
     * which is exactly the case an operator needs told. And `offWithHistory` is DELETED: with lookup
     * off the section shows this one sentence and no rows, including places rolled up while it was on.
     */
    off: 'No geo database is open on this server, so places are not shown.',
    other: (count: number) =>
      count === 0 ? null : `${plural(count, 'more request', 'more requests')} came from places outside this list.`,
    unresolved: (count: number) =>
      count === 0 ? null : `${plural(count, 'request', 'requests')} could not be placed.`,
    none: 'No request in this window could be placed.',
  },

  health: {
    title: 'API health',
    statusHeading: 'Responses',
    s2xx: '2xx · succeeded',
    s3xx: '3xx · redirected',
    s4xx: '4xx · refused',
    s4xxCaption: 'Includes routine sign-in expiry — apps refresh and retry',
    s5xx: '5xx · server errors',
    aborted: 'Closed before a response',
    codesHeading: 'Most common status codes',
    noCodes: 'No responses in this window.',
    latencyHeading: 'Response time',
    p50: 'Median (p50)',
    p95: '95th percentile (p95)',
    latencyCaption: 'Estimated from response-time buckets, not measured per request.',
    latency: (text: LatencyText) =>
      text.kind === 'none'
        ? '—'
        : text.kind === 'overflow'
          ? `> ${String(text.seconds)} s`
          : `${formatCount(text.ms)} ms`,
    errorsTitle: 'Errors over time',
    serverErrors: 'Server errors (5xx)',
    clientErrors: 'Refusals (4xx)',
    errorsSummary: (client: number, server: number, peak: Peak, grain: AnalyticsGrain) =>
      peak === null
        ? 'No refusals or server errors in this window.'
        : `${plural(server, 'server error', 'server errors')} and ${plural(client, 'refusal', 'refusals')} in this window. The most in one ${GRAIN_WORD[grain]} was ${formatCount(peak.total)}, at ${peak.label}.`,
    slowestHeading: 'Slowest routes',
    slowestCaption: `By 95th-percentile response time, among routes with at least ${String(ANALYTICS_SLOW_ROUTE_MIN_REQUESTS)} requests. A route pattern approximates a screen; it is not one.`,
    slowestRow: (requests: number) => `· ${plural(requests, 'request', 'requests')}`,
    noSlowest: `No route had ${String(ANALYTICS_SLOW_ROUTE_MIN_REQUESTS)} or more timed requests in this window.`,
    erroredHeading: 'Routes with the most errors',
    erroredRow: (client: number, server: number, requests: number) =>
      `· ${formatCount(client)} 4xx · ${formatCount(server)} 5xx of ${formatCount(requests)}`,
    noErrored: 'No route answered with an error in this window.',
  },

  security: {
    title: 'Security signals',
    topIpsHeading: 'Busiest addresses',
    /* [C5b §4] "with its place" only when there is a database to have found one. */
    topIpsCaption: (lookupEnabled: boolean) =>
      lookupEnabled
        ? 'Each address with its place and how many of its requests were rate-limited. Rate limits are counted since this release.'
        : 'Each address and how many of its requests were rate-limited. Rate limits are counted since this release.',
    /**
     * ⚠️ [amended C5b §4] A PLACE IS SHOWN OR ABSENT — NEVER "unknown", NEVER "not found". It was
     * `place ?? 'place unknown'`, so a server with no database open printed "place unknown" beside
     * every address, which reads as a lookup that ran and failed. The place part is omitted when the
     * lookup is off OR the address could not be placed; with neither a place nor a refusal there is
     * NO caption at all (`undefined`, which `RankedList` renders as nothing) rather than a lone `·`.
     */
    topIpCaption: (lookupEnabled: boolean, place: string | null, rateLimited: number): string | undefined => {
      const parts = [
        ...(lookupEnabled && place !== null ? [place] : []),
        ...(rateLimited > 0 ? [`${formatCount(rateLimited)} rate-limited`] : []),
      ];
      return parts.length === 0 ? undefined : `· ${parts.join(' · ')}`;
    },
    otherIps: (count: number) =>
      count === 0 ? null : `${plural(count, 'more request', 'more requests')} came from addresses outside this list.`,
    noIps: 'No addresses in this window.',
    failuresHeading: 'Refused requests, by kind',
    failuresCaption: 'Counted since this release.',
    routine: '(routine)',
    noFailures: 'No refused requests in this window.',
    kind: {
      otp_locked: 'Sign-in code attempts locked',
      otp_send_limited: 'Sign-in code requests limited',
      rate_limited: 'Rate limited',
      otp_code_rejected: 'Wrong sign-in code',
      refresh_rejected: 'Session refresh refused',
      token_expired: 'Expired sign-in',
      token_invalid: 'Invalid sign-in token',
      token_missing: 'No sign-in token',
      token_refused: 'Sign-in token refused',
      wrong_surface: 'Token for the other app',
      otp_refused: 'Sign-in refused',
      forbidden: 'Not permitted',
      otp_unknown_identifier: 'Unregistered number',
    } satisfies Record<AnalyticsAuthFailureKind, string>,
    clustersHeading: 'Addresses with repeated refusals',
    clustersCaption: `At least ${String(ANALYTICS_CLUSTER_MIN_FAILURES)} non-routine refusals from one address. Counted since this release.`,
    clusterRow: (topKind: string | null, more: number) =>
      topKind === null
        ? ''
        : more > 0
          ? `· mostly ${topKind.toLowerCase()}, +${String(more)} more kinds`
          : `· ${topKind.toLowerCase()}`,
    noClusters: 'No address reached the threshold in this window.',
    subnetsHeading: 'Networks with repeated refusals',
    subnetsCaption: `At least ${String(ANALYTICS_CLUSTER_MIN_FAILURES)} non-routine refusals across one IPv4 /24 or one IPv6 network (the prefix on the row). Counted since this release.`,
    subnetRow: (ips: number) => `· ${plural(ips, 'address', 'addresses')}`,
    noSubnets: 'No network reached the threshold in this window.',
    reuseHeading: 'Refresh-token reuse',
    reuseCaption: 'Counted since this release — previously written only to the server log.',
    reuseSummary: (total: number, peak: Peak, grain: AnalyticsGrain) =>
      peak === null
        ? 'No session was revoked for token reuse in this window.'
        : `${plural(total, 'session was', 'sessions were')} revoked because a refresh token was used twice. The most in one ${GRAIN_WORD[grain]} was ${formatCount(peak.total)}, at ${peak.label}.`,
    bansHeading: 'Sign-in lockouts',
    bansSet: 'Numbers locked out in this window',
    bansSetCaption: 'Counted since this release',
    bansActive: 'Locked out right now',
    bansActiveCaption: 'From the existing lockout records',
    bansActiveUnavailable: 'Unavailable',
    ipDetailRemoved: (dateTime: string) =>
      `Per-address detail before ${dateTime} IST has been removed by the retention rule, so the addresses and refusal kinds below cover only what came after it.`,
  },

  limits: {
    title: 'What this page cannot show',
    sentences: [
      'This page shows only what reached the API. It cannot show page views, time on screen, clicks or scrolling. Route patterns approximate screens; they are not screens.',
      'Duty Mitra and the native app are both counted as "Officer app".',
      'Unique officers and sessions are estimates (about ±1%).',
      'Failed sign-ins (401) include routine token expiry.',
      'An IP address locates a network, not a person.',
      /*
       * ⚠️ "ABOUT TWO MINUTES", NOT "ABOUT A MINUTE". A minute is rolled once it ended ninety
       * seconds ago and the tick runs every minute, so the newest minute on a rolled window is two
       * or three behind — `analyticsRead.service.ts` says so, and the page says when with
       * "Includes data through".
       */
      'Windows longer than 30 minutes are built from rollups and can lag by about two minutes.',
      'Requests stopped before reaching the API (by Cloudflare or the proxy) are not counted.',
    ],
  },

  series: {
    showFigures: 'Show the figures',
    time: (grain: AnalyticsGrain) =>
      grain === 'minute' ? 'Minute (IST)' : grain === 'hour' ? 'Hour (IST)' : 'Day (IST)',
  },

  /*
   * ⚠️ [ANALYTICS §6] THE OFFICER TRAIL PANEL. The audit sentence is rendered BEFORE the button and never
   * behind it: the viewer must know the look is recorded before they take it, not after.
   *
   * ⚠️ "IN USE FROM", NOT "FIRST SEEN". An address's time is when it most recently BECAME the one the
   * officer's requests came from — an officer who went A → B → A shows A at its SECOND arrival — so
   * "first seen" would state a date the record does not hold.
   */
  trail: {
    title: "Officer's trail",
    intro: `The last ${String(OFFICER_TRAIL_MAX_ENTRIES)} distinct addresses an officer’s requests came from, and the last ${String(OFFICER_LOCATION_MAX_ENTRIES)} locations the officer app sent.`,
    auditNotice: "Opening an officer's trail is recorded in the audit log under your name.",
    officerLabel: 'Officer',
    show: 'Show trail',
    failed: 'The trail could not be opened. Nothing was shown, and you can try again.',
    inactive: '(inactive)',
    addressesHeading: 'Addresses',
    addressesCaption: 'Newest first. The time is when that address became the one their requests came from.',
    address: (ip: string, since: string) => `${ip} · in use from ${since} IST`,
    noAddresses: 'No addresses are recorded for this officer.',
    locationsHeading: 'Locations',
    locationsCaption: 'Newest first. Each is a location the officer app sent, whether or not a duty was in progress.',
    coordinates: (lat: string, lng: string) => `${lat}, ${lng}`,
    accuracy: (metres: number | null) =>
      metres === null ? 'accuracy unknown' : `±${formatCount(Math.round(metres))} m`,
    recorded: (at: string) => `recorded ${at} IST`,
    /* ⚠️ The PHONE's clock, named as such — it may be wrong, and nothing on the server decides on it. */
    deviceTime: (at: string) => `phone's clock ${at} IST`,
    noLocations: 'No locations are recorded for this officer.',
    retention: (days: number) => `Entries older than ${formatCount(days)} days are removed automatically.`,
  },
} as const;
